import { c as _c } from "react/compiler-runtime";
import { InlineList } from '@/components/Atoms/InlineList/InlineList';
import { LinkAwareContainer } from '@/components/Atoms/LinkAwareContainer/LinkAwareContainer';
import { H5 } from '@/components/Core/Heading';
import { ImageWithToaster } from '@/components/Molecules/ImageWithToaster/ImageWithToaster';
import { TrackableExternalLink } from '@/components/Molecules/TrackableExternalLink/TrackableExternalLink';
import useTranslation from '@/hooks/useTranslation';
import type { IImage, ILink, IToasterData } from '@/interfaces';
import { ResponsiveImageHints } from '@/services/ResponsiveImageHints';
import classNames from '@/utils/classNames';
import type { ComponentProps, ElementType, FC } from 'react';
import styles from './ImageWithSource.module.css';
interface IProps extends ComponentProps<'figure'> {
  /** Title of this image */
  title?: string;
  /** Owner/source of this image */
  publicationSource?: string;
  /** Note of the image */
  publicationNote?: string;
  /** The URL to access the resource */
  image: IImage;
  /** A list of link descriptors to include in the figure */
  links?: ILink[];
  /** Styled component modifier */
  as?: ElementType;
  imageToasterData?: IToasterData;
  colorScheme?: ComponentProps<typeof ImageWithToaster>['colorScheme'];
}
const imageSizeHints = new ResponsiveImageHints({
  initial: '100vw',
  atXXL: '768px'
});

/**
 * A sourced figure with a link set and heading.
 */
export const ImageWithSource: FC<IProps> = t0 => {
  const $ = _c(36);
  let className;
  let colorScheme;
  let image;
  let imageToasterData;
  let links;
  let publicationNote;
  let publicationSource;
  let rest;
  let t1;
  let title;
  if ($[0] !== t0) {
    ({
      title,
      publicationSource,
      publicationNote,
      links,
      image,
      imageToasterData,
      colorScheme,
      as: t1,
      className,
      ...rest
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = colorScheme;
    $[3] = image;
    $[4] = imageToasterData;
    $[5] = links;
    $[6] = publicationNote;
    $[7] = publicationSource;
    $[8] = rest;
    $[9] = t1;
    $[10] = title;
  } else {
    className = $[1];
    colorScheme = $[2];
    image = $[3];
    imageToasterData = $[4];
    links = $[5];
    publicationNote = $[6];
    publicationSource = $[7];
    rest = $[8];
    t1 = $[9];
    title = $[10];
  }
  const as = t1 === undefined ? null : t1;
  const {
    t
  } = useTranslation();
  let t2;
  if ($[11] !== className) {
    t2 = classNames(styles.container, className);
    $[11] = className;
    $[12] = t2;
  } else {
    t2 = $[12];
  }
  let t3;
  if ($[13] !== as || $[14] !== title) {
    t3 = title && <H5 as={as}>{title}</H5>;
    $[13] = as;
    $[14] = title;
    $[15] = t3;
  } else {
    t3 = $[15];
  }
  let t4;
  if ($[16] !== colorScheme || $[17] !== image || $[18] !== imageToasterData) {
    t4 = <ImageWithToaster colorScheme={colorScheme} alt="" src={image} sizeHints={imageSizeHints} toasterData={imageToasterData} />;
    $[16] = colorScheme;
    $[17] = image;
    $[18] = imageToasterData;
    $[19] = t4;
  } else {
    t4 = $[19];
  }
  let t5;
  if ($[20] !== publicationSource || $[21] !== t) {
    t5 = publicationSource && <figcaption>{t("Common.source", {
        source: publicationSource
      })}</figcaption>;
    $[20] = publicationSource;
    $[21] = t;
    $[22] = t5;
  } else {
    t5 = $[22];
  }
  let t6;
  if ($[23] !== publicationNote || $[24] !== t) {
    t6 = publicationNote && <figcaption>{t("Common.note", {
        note: publicationNote
      })}</figcaption>;
    $[23] = publicationNote;
    $[24] = t;
    $[25] = t6;
  } else {
    t6 = $[25];
  }
  let t7;
  if ($[26] !== links) {
    t7 = links?.length > 0 && <LinkAwareContainer><InlineList>{links.map(_temp)}</InlineList></LinkAwareContainer>;
    $[26] = links;
    $[27] = t7;
  } else {
    t7 = $[27];
  }
  let t8;
  if ($[28] !== rest || $[29] !== t2 || $[30] !== t3 || $[31] !== t4 || $[32] !== t5 || $[33] !== t6 || $[34] !== t7) {
    t8 = <figure {...rest} className={t2}>{t3}{t4}{t5}{t6}{t7}</figure>;
    $[28] = rest;
    $[29] = t2;
    $[30] = t3;
    $[31] = t4;
    $[32] = t5;
    $[33] = t6;
    $[34] = t7;
    $[35] = t8;
  } else {
    t8 = $[35];
  }
  return t8;
};
function _temp(link, rIdx) {
  return <li key={`__exhibitLink__${rIdx}`}>{link.url && <TrackableExternalLink href={link.url} label={link.label} />}{!link.url && link.label}</li>;
}